import { useState, useRef, useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { apiClient } from '../api/client';
import type { WpSite } from '@audit/shared-types';

interface ChatMessage {
  role: 'user' | 'assistant';
  content: string;
}

export default function AiQueryPage() {
  const [messages, setMessages] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [siteId, setSiteId] = useState<number | undefined>();
  const bottomRef = useRef<HTMLDivElement>(null);

  const { data: sites = [] } = useQuery({
    queryKey: ['sites'],
    queryFn: () => apiClient.get<WpSite[]>('/sites').then((r) => r.data),
  });

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading]);

  async function send() {
    const question = input.trim();
    if (!question || loading) return;
    setInput('');
    setMessages((prev) => [...prev, { role: 'user', content: question }]);
    setLoading(true);
    try {
      const res = await apiClient.post<{ answer: string }>('/ai/query', {
        question,
        ...(siteId != null ? { siteId } : {}),
      });
      setMessages((prev) => [...prev, { role: 'assistant', content: res.data.answer }]);
    } catch {
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: '⚠️ AI unavailable — please try again.' },
      ]);
    } finally {
      setLoading(false);
    }
  }

  const STARTER_QUESTIONS = [
    'What are our most common WCAG violations?',
    'Which pages have the most critical issues?',
    'Which blocks appear on the most pages?',
    'What should we fix first to improve accessibility?',
  ];

  return (
    <div className="flex flex-col" style={{ height: 'calc(100vh - 8rem)' }}>
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">AI Query</h2>
          <p className="text-sm text-gray-500 mt-0.5">
            Ask questions about your audit data and block usage in plain English
          </p>
        </div>
        <div className="flex items-center gap-3">
          <select
            value={siteId ?? ''}
            onChange={(e) => setSiteId(e.target.value ? Number(e.target.value) : undefined)}
            className="text-sm border border-gray-300 rounded-lg px-3 py-1.5 text-gray-700 bg-white"
          >
            <option value="">All sites</option>
            {sites.map((s) => (
              <option key={s.id} value={s.id}>
                {s.name}
              </option>
            ))}
          </select>
          {messages.length > 0 && (
            <button
              onClick={() => setMessages([])}
              className="text-xs text-gray-500 hover:text-gray-700 border border-gray-300 px-2.5 py-1.5 rounded-lg"
            >
              Clear
            </button>
          )}
        </div>
      </div>

      {/* Message area */}
      <div className="flex-1 overflow-auto bg-white border border-gray-200 rounded-xl p-5 mb-4 space-y-4">
        {messages.length === 0 && (
          <div className="flex flex-col items-center justify-center h-full text-center py-8">
            <p className="text-4xl mb-3">💬</p>
            <p className="text-sm text-gray-600 font-medium mb-1">
              Ask anything about your accessibility audits or block usage
            </p>
            <p className="text-xs text-gray-400 mb-6">
              The AI has access to your recent audit results, issue patterns, and block usage data.
            </p>
            <div className="grid grid-cols-2 gap-2 w-full max-w-xl">
              {STARTER_QUESTIONS.map((q) => (
                <button
                  key={q}
                  onClick={() => setInput(q)}
                  className="text-xs text-left text-blue-700 bg-blue-50 border border-blue-200 rounded-lg px-3 py-2 hover:bg-blue-100"
                >
                  {q}
                </button>
              ))}
            </div>
          </div>
        )}

        {messages.map((msg, i) => (
          <div
            key={i}
            className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
          >
            <div
              className={`max-w-[80%] rounded-2xl px-4 py-3 text-sm leading-relaxed whitespace-pre-wrap ${
                msg.role === 'user'
                  ? 'bg-blue-600 text-white rounded-br-sm'
                  : 'bg-gray-100 text-gray-800 rounded-bl-sm'
              }`}
            >
              {msg.content}
            </div>
          </div>
        ))}

        {loading && (
          <div className="flex justify-start">
            <div className="bg-gray-100 rounded-2xl rounded-bl-sm px-4 py-3">
              <div className="flex gap-1 items-center">
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '0ms' }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '150ms' }}
                />
                <span
                  className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                  style={{ animationDelay: '300ms' }}
                />
              </div>
            </div>
          </div>
        )}
        <div ref={bottomRef} />
      </div>

      {/* Input */}
      <div className="flex gap-3">
        <input
          type="text"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
              e.preventDefault();
              void send();
            }
          }}
          placeholder="Ask about your audit data…"
          disabled={loading}
          className="flex-1 text-sm border border-gray-300 rounded-xl px-4 py-3 focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-50"
        />
        <button
          onClick={() => void send()}
          disabled={!input.trim() || loading}
          className="text-sm bg-blue-600 text-white px-5 py-3 rounded-xl hover:bg-blue-700 disabled:opacity-50 font-medium"
        >
          Send
        </button>
      </div>
    </div>
  );
}
